import React, { useState, useEffect, useRef } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, Input, Textarea, FormControl, useToast, VStack,
  HStack, Select, Checkbox, Text, Box, Divider, Badge, IconButton, Flex, FormLabel,
} from '@chakra-ui/react';
import { FiX } from 'react-icons/fi';
import api from '../api';

const DAYS_OF_WEEK = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

function EyebrowLabel({ children }) {
  return (
    <span style={{ display:'inline-block', background:'#2d3748', color:'white', padding:'2px 6px', borderRadius:'3px', marginBottom:'4px',
      fontSize:'8px', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', lineHeight:1 }}>
      {children}
    </span>
  );
}

export default function TaskEditModal({ isOpen, onClose, taskTitle, jobDescription, taskData, onSave }) {
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [startDate, setStartDate] = useState('');
  const [deadlineDate, setDeadlineDate] = useState('');
  const [deadlineType, setDeadlineType] = useState('flexible');
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrenceType, setRecurrenceType] = useState('weekly');
  const [recurrenceInterval, setRecurrenceInterval] = useState('');
  const [recurrenceDay, setRecurrenceDay] = useState('');
  const [saving, setSaving] = useState(false);
  const [taskContacts, setTaskContacts] = useState([]);
  const [taskUsers, setTaskUsers] = useState([]);
  const [taskCompanies, setTaskCompanies] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [companyContacts, setCompanyContacts] = useState([]);
  const [companySearch, setCompanySearch] = useState('');
  const [companyResults, setCompanyResults] = useState([]);
  const [taskDeps, setTaskDeps] = useState([]);
  const [siblingTasks, setSiblingTasks] = useState([]);
  const [poRequired, setPoRequired] = useState(false);
  const [invoicingNotes, setInvoicingNotes] = useState('');
  const toast = useToast();
  const wasOpen = useRef(false);

  const taskId = taskData?.id;
  const companyId = taskData?.company_id;

  const fetchTaskContacts = async () => { if (!taskId) return; try { const { data } = await api.get(`/tasks/${taskId}/contacts`); setTaskContacts(data); } catch(e) {} };
  const fetchTaskUsers = async () => { if (!taskId) return; try { const { data } = await api.get(`/tasks/${taskId}/users`); setTaskUsers(data); } catch(e) {} };
  const fetchTaskCompanies = async () => { if (!taskId) return; try { const { data } = await api.get(`/tasks/${taskId}/companies`); setTaskCompanies(data); } catch(e) {} };
  const fetchAllUsers = async () => { try { const { data } = await api.get('/calendar/users'); setAllUsers(data); } catch(e) {} };
  const fetchCompanyContacts = async () => { if (!companyId) return; try { const { data } = await api.get(`/contacts?company_id=${companyId}`); setCompanyContacts(data); } catch(e) {} };
  const fetchTaskDeps = async () => { if (!taskId) return; try { const { data } = await api.get(`/tasks/${taskId}/dependencies`); setTaskDeps(data); } catch(e) {} };
  const fetchSiblingTasks = async () => {
    if (!taskData?.project_id) return;
    try { const { data } = await api.get(`/tasks?project_id=${taskData.project_id}`); setSiblingTasks((data || []).filter(t => t.id !== taskId)); } catch(e) {}
  };

  const searchCompanies = async (q) => {
    if (q.length < 1) { setCompanyResults([]); return; }
    try { const { data } = await api.get(`/companies/search?q=${encodeURIComponent(q)}&limit=8`); setCompanyResults(data.filter(c => !taskCompanies.find(tc => tc.company_id === c.id))); } catch(e) {}
  };

  useEffect(() => {
    // CRITICAL: Only initialize form when modal OPENS (false→true transition).
    // Never re-initialize while the modal is already open — that wipes user input.
    if (isOpen && !wasOpen.current) {
      setTitle(taskTitle || '');
      setDesc(jobDescription || '');
      if (taskData) {
        setStartDate(taskData.start_date ? taskData.start_date.split('T')[0] : '');
        setDeadlineDate(taskData.deadline_date ? taskData.deadline_date.split('T')[0] : '');
        setDeadlineType(taskData.deadline_type || 'flexible');
        setIsRecurring(!!taskData.is_recurring);
        setRecurrenceType(taskData.recurrence_type || 'weekly');
        setRecurrenceInterval(taskData.recurrence_interval || '');
        setRecurrenceDay(taskData.recurrence_day !== null && taskData.recurrence_day !== undefined ? String(taskData.recurrence_day) : '');
        setPoRequired(!!taskData.po_required);
        setInvoicingNotes(taskData.invoicing_notes || '');
      } else {
        setStartDate(''); setDeadlineDate(''); setDeadlineType('flexible');
        setIsRecurring(false); setRecurrenceType('weekly'); setRecurrenceInterval(''); setRecurrenceDay('');
        setPoRequired(false); setInvoicingNotes('');
      }
      setCompanySearch(''); setCompanyResults([]);
      fetchTaskContacts(); fetchTaskUsers(); fetchTaskCompanies(); fetchAllUsers(); fetchCompanyContacts();
      fetchTaskDeps(); fetchSiblingTasks();
    }
    wasOpen.current = isOpen;
  }, [isOpen]); // eslint-disable-line

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        task_title: title, job_description: desc,
        start_date: startDate || null, deadline_date: deadlineDate || null, deadline_type: deadlineType,
        is_recurring: isRecurring, recurrence_type: isRecurring ? recurrenceType : null,
        recurrence_interval: isRecurring && recurrenceType === 'days' ? parseInt(recurrenceInterval) || null : null,
        recurrence_day: isRecurring && recurrenceType === 'weekly' ? parseInt(recurrenceDay) : null,
        next_recurrence_date: null,
        po_required: poRequired,
        invoicing_notes: invoicingNotes || null,
      });
      toast({ title: 'Task updated', status: 'success', duration: 1500 });
      onClose();
    } catch (err) {
      toast({ title: 'Failed to save', status: 'error', duration: 2000 });
    } finally { setSaving(false); }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="90vh">
        <ModalHeader fontSize="md">Edit Task</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex gap={5} align="flex-start">
            {/* ── LEFT COLUMN ── */}
            <VStack spacing={3} flex={1} minW={0}>
            <FormControl>
              <EyebrowLabel>Task Title</EyebrowLabel>
              <Input size="sm" value={title} onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter task title..." autoFocus />
            </FormControl>
            <FormControl>
              <EyebrowLabel>Description / Brief</EyebrowLabel>
              <Textarea size="sm" rows={5} value={desc} onChange={(e) => setDesc(e.target.value)}
                placeholder="Enter description or brief..." />
            </FormControl>

            <HStack w="100%" spacing={2}>
              <FormControl flex={1}>
                <EyebrowLabel>Start Date</EyebrowLabel>
                <Input size="sm" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </FormControl>
              <FormControl flex={1}>
                <EyebrowLabel>Deadline</EyebrowLabel>
                <Input size="sm" type="date" value={deadlineDate} onChange={(e) => setDeadlineDate(e.target.value)} />
              </FormControl>
              <FormControl flex={1}>
                <EyebrowLabel>Deadline Type</EyebrowLabel>
                <Select size="sm" value={deadlineType} onChange={(e) => setDeadlineType(e.target.value)}>
                  <option value="flexible">Flexible</option>
                  <option value="hard">Hard</option>
                </Select>
              </FormControl>
            </HStack>

            <Divider />

            <FormControl>
              <Checkbox size="sm" isChecked={isRecurring} onChange={(e) => setIsRecurring(e.target.checked)}
                colorScheme="purple">
                <Text fontSize="12px" fontWeight="600">Recurring Task</Text>
              </Checkbox>
              {isRecurring && (
                <Text fontSize="9px" color="gray.500" mt={0.5}>
                  A new copy of this task (title + description only) will be created at each interval while the project is active.
                </Text>
              )}
            </FormControl>

            {isRecurring && (
              <Box bg="purple.50" p={3} borderRadius="md" w="100%">
                <VStack spacing={2}>
                  <HStack w="100%" spacing={2}>
                    <FormControl flex={1}>
                      <EyebrowLabel>Repeat Every</EyebrowLabel>
                      <Select size="sm" value={recurrenceType} onChange={(e) => setRecurrenceType(e.target.value)}>
                        <option value="daily">Daily</option>
                        <option value="days">Every X Days</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="biannual">Bi-Annual (6 months)</option>
                        <option value="yearly">Yearly</option>
                      </Select>
                    </FormControl>

                    {recurrenceType === 'days' && (
                      <FormControl flex={1}>
                        <EyebrowLabel>Every X Days</EyebrowLabel>
                        <Input size="sm" type="number" min="1" value={recurrenceInterval}
                          onChange={(e) => setRecurrenceInterval(e.target.value)} placeholder="e.g. 14" />
                      </FormControl>
                    )}

                    {recurrenceType === 'weekly' && (
                      <FormControl flex={1}>
                        <EyebrowLabel>Day of Week</EyebrowLabel>
                        <Select size="sm" value={recurrenceDay} onChange={(e) => setRecurrenceDay(e.target.value)}>
                          <option value="">Same day</option>
                          {DAYS_OF_WEEK.map((d, i) => <option key={i} value={i}>{d}</option>)}
                        </Select>
                      </FormControl>
                    )}
                  </HStack>

                  {!startDate && (
                    <Text fontSize="10px" color="orange.500" fontWeight="600">
                      Set a Start Date above to activate recurring schedule.
                    </Text>
                  )}
                </VStack>
              </Box>
            )}

            </VStack>
            {/* ── RIGHT COLUMN ── */}
            <VStack spacing={3} w="280px" flexShrink={0}>
            {/* Task Companies */}
            {taskId && (
              <>
              <Box w="100%">
                <EyebrowLabel>Companies Assigned to Task</EyebrowLabel>
                <Flex gap={1} flexWrap="wrap" mb={1}>
                  {taskCompanies.map(tc => (
                    <Badge key={tc.company_id} colorScheme="purple" fontSize="10px" display="flex" alignItems="center" gap={1}>
                      {tc.name}
                      <IconButton icon={<FiX />} size="xs" variant="ghost" minW="14px" h="14px"
                        onClick={async () => { await api.delete(`/tasks/${taskId}/companies/${tc.company_id}`); fetchTaskCompanies(); }} aria-label="Remove" />
                    </Badge>
                  ))}
                  {taskCompanies.length === 0 && <Text color="gray.400">None</Text>}
                </Flex>
                <Input size="xs" placeholder="Search company to add..." value={companySearch}
                  onChange={(e) => { setCompanySearch(e.target.value); searchCompanies(e.target.value); }} />
                {companyResults.length > 0 && (
                  <Box border="1px" borderColor="gray.200" borderRadius="md" mt={1} maxH="120px" overflowY="auto" bg="white">
                    {companyResults.map(c => (
                      <Box key={c.id} px={2} py={1} fontSize="11px" cursor="pointer" _hover={{ bg: 'blue.50' }}
                        onClick={async () => { await api.post(`/tasks/${taskId}/companies`, { company_id: c.id }); setCompanySearch(''); setCompanyResults([]); fetchTaskCompanies(); }}>
                        {c.name}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
              </>
            )}

            {/* Task Contacts */}
            {taskId && (
              <>
              <Divider />
              <Box w="100%">
                <EyebrowLabel>Contacts Assigned to Task</EyebrowLabel>
                <Flex gap={1} flexWrap="wrap" mb={1}>
                  {taskContacts.map(tc => (
                    <Badge key={tc.contact_id} colorScheme="blue" fontSize="10px" display="flex" alignItems="center" gap={1}>
                      {tc.first_name} {tc.last_name || ''} {tc.company_name ? `(${tc.company_name})` : ''}
                      <IconButton icon={<FiX />} size="xs" variant="ghost" minW="14px" h="14px"
                        onClick={async () => { await api.delete(`/tasks/${taskId}/contacts/${tc.contact_id}`); fetchTaskContacts(); }} aria-label="Remove" />
                    </Badge>
                  ))}
                  {taskContacts.length === 0 && <Text color="gray.400">None</Text>}
                </Flex>
                <Select size="xs" placeholder="Add contact..." value=""
                  onChange={async (e) => { if (e.target.value) { await api.post(`/tasks/${taskId}/contacts`, { contact_id: e.target.value }); fetchTaskContacts(); } }}>
                  {companyContacts.filter(c => !taskContacts.find(tc => tc.contact_id === c.id)).map(c => (
                    <option key={c.id} value={c.id}>{c.first_name} {c.last_name || ''}</option>
                  ))}
                </Select>
              </Box>
              </>
            )}

            {/* Task Users */}
            {taskId && (
              <>
              <Box w="100%">
                <EyebrowLabel>Users Assigned to Task</EyebrowLabel>
                <Flex gap={1} flexWrap="wrap" mb={1}>
                  {taskUsers.map(tu => (
                    <Badge key={tu.user_id} colorScheme="teal" fontSize="10px" display="flex" alignItems="center" gap={1}>
                      {tu.display_name || tu.username}
                      <IconButton icon={<FiX />} size="xs" variant="ghost" minW="14px" h="14px"
                        onClick={async () => { await api.delete(`/tasks/${taskId}/users/${tu.user_id}`); fetchTaskUsers(); }} aria-label="Remove" />
                    </Badge>
                  ))}
                  {taskUsers.length === 0 && <Text color="gray.400">None</Text>}
                </Flex>
                <Select size="xs" placeholder="Add user..." value=""
                  onChange={async (e) => { if (e.target.value) { await api.post(`/tasks/${taskId}/users`, { user_id: e.target.value }); fetchTaskUsers(); } }}>
                  {allUsers.filter(u => !taskUsers.find(tu => tu.user_id === u.id)).map(u => (
                    <option key={u.id} value={u.id}>{u.display_name || u.username}</option>
                  ))}
                </Select>
              </Box>
              </>
            )}

            {/* Task Dependencies */}
            {taskId && (
              <>
              <Divider />
              <Box w="100%">
                <EyebrowLabel>Task Dependencies</EyebrowLabel>
                <Flex gap={1} flexWrap="wrap" mb={1}>
                  {taskDeps.map(dep => (
                    <Badge key={dep.id} fontSize="10px" display="flex" alignItems="center" gap={1}
                      colorScheme={dep.depends_on_complete ? 'green' : 'orange'}
                      variant={dep.depends_on_complete ? 'subtle' : 'solid'}>
                      {dep.dependency_type === 'blocks' ? '🔒' : '⇄'} {dep.depends_on_title || 'Task #' + dep.depends_on_task_id}
                      {dep.depends_on_complete && ' ✓'}
                      <IconButton icon={<FiX />} size="xs" variant="ghost" minW="14px" h="14px"
                        onClick={async () => { await api.delete(`/tasks/${taskId}/dependencies/${dep.id}`); fetchTaskDeps(); }} aria-label="Remove" />
                    </Badge>
                  ))}
                  {taskDeps.length === 0 && <Text color="gray.400">No dependencies</Text>}
                </Flex>
                <HStack spacing={1}>
                  <Select size="xs" placeholder="Add dependency..." value="" flex={1}
                    onChange={async (e) => {
                      if (e.target.value) {
                        const [depTaskId, depType] = e.target.value.split('|');
                        await api.post(`/tasks/${taskId}/dependencies`, { depends_on_task_id: depTaskId, dependency_type: depType || 'blocks' });
                        fetchTaskDeps();
                      }
                    }}>
                    {siblingTasks.filter(t => !taskDeps.find(d => d.depends_on_task_id === t.id)).map(t => (
                      <option key={t.id} value={`${t.id}|blocks`}>
                        {t.task_title || 'Untitled'} {t.is_complete ? '(done)' : ''}
                      </option>
                    ))}
                  </Select>
                </HStack>
                {taskDeps.some(d => !d.depends_on_complete && d.dependency_type === 'blocks') && (
                  <Text fontSize="9px" color="orange.500" mt={1}>
                    ⚠ This task is blocked by incomplete dependencies
                  </Text>
                )}
              </Box>
              </>
            )}

            {/* Invoicing Workflow */}
            <Divider />
            <Box w="100%" bg="cyan.50" p={3} borderRadius="md" border="1px" borderColor="cyan.200">
              <EyebrowLabel>Invoicing Workflow</EyebrowLabel>
              <VStack spacing={2} mt={2}>
                <Checkbox size="sm" isChecked={poRequired} onChange={e => setPoRequired(e.target.checked)} colorScheme="cyan" w="100%">
                  <Text fontSize="12px" fontWeight="600" color="cyan.800">PO Required for this task</Text>
                </Checkbox>
                <FormControl>
                  <Text fontSize="9px" fontWeight="700" color="gray.500" textTransform="uppercase" mb={0.5}>Invoicing Notes</Text>
                  <Textarea size="sm" rows={2} value={invoicingNotes} onChange={e => setInvoicingNotes(e.target.value)}
                    placeholder="e.g. Send invoice to accounts@client.com, quote PO number on all docs..." />
                </FormControl>
              </VStack>
            </Box>
            </VStack>
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Button size="sm" variant="ghost" mr={2} onClick={onClose}>Cancel</Button>
          <Button size="sm" bg="brand.600" color="white" _hover={{ bg: 'brand.700' }}
            onClick={handleSave} isLoading={saving}>Save</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
